import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X, HelpCircle, Lightbulb } from 'lucide-react';
import { FeedbackModal } from './FeedbackModal';
import { HelpPanel } from './HelpPanel';

const DEFAULT_CONFIG = {
  position: 'bottom-right',
  primaryColor: '#7c3aed', // violet-600
  help: {
    default: {
      title: 'Need Help?',
      content: 'We\'re here to help! Check out our documentation or contact support.',
      docsUrl: '/help',
      contactEmail: 'support@acquiva.com'
    }
  }
};

export function FeedbackWidget({ config = {} }) {
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };
  const location = useLocation();
  const pathname = location.pathname;
  
  const [isOpen, setIsOpen] = useState(false);
  const [activePanel, setActivePanel] = useState(null);
  const widgetRef = useRef(null);

  // Close when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (widgetRef.current && !widgetRef.current.contains(event.target)) {
        setIsOpen(false);
        setActivePanel(null);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get position classes
  const positionClasses = {
    'bottom-right': 'bottom-6 right-6',
    'bottom-left': 'bottom-6 left-6',
    'top-right': 'top-6 right-6',
    'top-left': 'top-6 left-6',
  }[mergedConfig.position || 'bottom-right'];

  // Get help content for current page
  const getHelpContent = () => {
    if (mergedConfig.help?.pages) {
      // Check for exact match first
      if (mergedConfig.help.pages[pathname]) {
        return mergedConfig.help.pages[pathname];
      }
      // Check for pattern matches
      for (const [pattern, content] of Object.entries(mergedConfig.help.pages)) {
        if (pattern.includes('*')) {
          const regex = new RegExp('^' + pattern.replace(/\*/g, '.*') + '$');
          if (regex.test(pathname)) {
            return content;
          }
        }
      }
    }
    return mergedConfig.help?.default;
  };

  const handleToggle = () => {
    if (isOpen) {
      setIsOpen(false);
      setActivePanel(null);
    } else {
      setIsOpen(true);
      setActivePanel('menu');
    }
  };

  const handleHelpClick = () => {
    setActivePanel('help');
    mergedConfig.onHelpOpen?.(pathname);
  };

  const handleFeedbackClick = () => {
    setActivePanel('feedback');
  };

  const handleClose = () => {
    setIsOpen(false);
    setActivePanel(null);
  };

  const handleFeedbackSubmit = (feedback) => {
    mergedConfig.onFeedbackSubmit?.(feedback);
    handleClose();
  };

  return (
    <div 
      ref={widgetRef}
      className={`fixed ${positionClasses} z-50`}
      style={{ '--feedback-primary': mergedConfig.primaryColor }}
    >
      {/* Feedback Modal */}
      {activePanel === 'feedback' && (
        <FeedbackModal
          config={mergedConfig}
          currentPath={pathname}
          onSubmit={handleFeedbackSubmit}
          onClose={handleClose}
        />
      )}

      {/* Help Panel */}
      {activePanel === 'help' && (
        <HelpPanel
          content={getHelpContent()}
          onClose={handleClose}
        />
      )}

      {/* Menu Options */}
      {activePanel === 'menu' && (
        <div className="mb-3 flex flex-col gap-2 animate-in fade-in slide-in-from-bottom-2 duration-200">
          <button
            onClick={handleHelpClick}
            className="flex items-center gap-3 px-4 py-3 bg-white rounded-xl shadow-lg border border-neutral-200 hover:border-violet-300 hover:shadow-xl transition-all group"
          >
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
              <HelpCircle className="w-5 h-5 text-blue-600" />
            </div>
            <div className="text-left">
              <div className="font-medium text-neutral-900">Get Help</div>
              <div className="text-xs text-neutral-500">Documentation & support</div>
            </div>
          </button>

          <button
            onClick={handleFeedbackClick}
            className="flex items-center gap-3 px-4 py-3 bg-white rounded-xl shadow-lg border border-neutral-200 hover:border-violet-300 hover:shadow-xl transition-all group"
          >
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center group-hover:bg-amber-200 transition-colors">
              <Lightbulb className="w-5 h-5 text-amber-600" />
            </div>
            <div className="text-left">
              <div className="font-medium text-neutral-900">Make Suggestion</div>
              <div className="text-xs text-neutral-500">Share feedback or ideas</div>
            </div>
          </button>
        </div>
      )}

      {/* Main Toggle Button */}
      <button
        onClick={handleToggle}
        className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-105 ${
          isOpen 
            ? 'bg-neutral-800 hover:bg-neutral-700' 
            : 'bg-violet-600 hover:bg-violet-700'
        }`}
        style={{ backgroundColor: isOpen ? undefined : mergedConfig.primaryColor }}
        aria-label={isOpen ? 'Close feedback menu' : 'Open feedback menu'}
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <MessageCircle className="w-6 h-6 text-white" />
        )}
      </button>
    </div>
  );
}

